/** Who did what to this integration, and when, newest first. */
import { History } from 'lucide-react';
import { useEffect, useState } from 'react';

import { adminClient, type IntegrationActivityEntry } from '@/api/adminClient';
import { Button } from '@/components/ui/button';
import { LoadError, Skeleton, SkeletonList } from '@/components/ui/load-state';
import { resolveLoadStatus } from '@/lib/loadState';

import { activityActor, activitySentence, relativeTime } from './builderModel';

const COLLAPSED = 6;

export function ActivityPanel({ draftId, updatedAt }: { draftId: string; updatedAt: string }) {
  const [items, setItems] = useState<IntegrationActivityEntry[]>();
  const [expanded, setExpanded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    adminClient
      .getIntegrationDraftActivity(draftId)
      .then((value) => {
        if (!cancelled) setItems(value.items);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [draftId, updatedAt, reloadKey]);

  const status = resolveLoadStatus({ loaded: Boolean(items) || failed, error: failed, hasData: Boolean(items) });

  const shown = expanded ? items : items?.slice(0, COLLAPSED);

  return (
    <section aria-label="Activity" className="mt-10 overflow-hidden rounded-xl border border-border">
      <div className="flex items-center gap-2 bg-card/40 px-5 py-3 text-sm font-semibold">
        <History className="h-4 w-4 text-muted-foreground" />
        Activity
      </div>
      {status === 'loading' ? (
        <SkeletonList
          rows={3}
          rowClassName="border-t border-border px-5 py-3"
          renderRow={() => <Skeleton className="h-5 w-2/3" />}
        />
      ) : status === 'error' || !items ? (
        <LoadError compact className="border-t border-border px-5 py-3" onRetry={() => setReloadKey((key) => key + 1)} />
      ) : items.length === 0 ? (
        <p className="border-t border-border px-5 py-3 text-xs text-muted-foreground">Nothing recorded yet.</p>
      ) : (
        <ol>
          {shown?.map((entry, index) => (
            <li key={`${entry.at}-${entry.action}-${index}`} className="border-t border-border px-5 py-3 text-sm">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span>
                  <span className="font-medium">{activityActor(entry)}</span> {activitySentence(entry)}
                </span>
                <time dateTime={entry.at} title={new Date(entry.at).toLocaleString()} className="text-xs text-muted-foreground">
                  {relativeTime(entry.at)}
                </time>
              </div>
              {entry.note ? <p className="mt-1 whitespace-pre-wrap text-xs text-muted-foreground">“{entry.note}”</p> : null}
              {entry.details.length ? (
                <ul className="mt-1 list-disc space-y-0.5 pl-5 text-xs text-muted-foreground">
                  {entry.details.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              ) : null}
            </li>
          ))}
        </ol>
      )}
      {items && items.length > COLLAPSED ? (
        <div className="border-t border-border px-5 py-2">
          <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={() => setExpanded((value) => !value)}>
            {expanded ? 'Show less' : `Show all ${items.length}`}
          </Button>
        </div>
      ) : null}
    </section>
  );
}
